<script setup lang="ts">
import {
  BookOpen,
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  ClipboardCheck,
  FileText,
  Heart,
  LayoutDashboard,
  ListFilter,
  LogIn,
  LogOut,
  Menu,
  Microscope,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  UserPlus,
  Workflow,
  X
} from '@lucide/vue';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { normalizeCandidateSearch } from './candidate-presentation.js';
import { SNAPSHOT_PUBLISH_SCHEDULE_LABEL, displayDate, displayDateTime, displaySignedPct } from './format.js';
import { publicResearchDataStatus } from './platform-status.js';
import LoadState from './components/LoadState.vue';
import { userFacingError } from './api.js';
import { usePlatformStore } from './store.js';

const store = usePlatformStore();
const route = useRoute();
const router = useRouter();
const mobileOpen = ref(false);
const searchQuery = ref('');
const shellMessage = ref('');
let returnFocus: HTMLElement | null = null;
let originalOverflow = '';
watch(mobileOpen, async (open) => {
  if (open) {
    returnFocus = document.activeElement as HTMLElement | null;
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    await nextTick();
    document.querySelector<HTMLElement>('.mobile-drawer button')?.focus();
  } else {
    document.body.style.overflow = originalOverflow;
    returnFocus?.focus();
  }
});
function drawerKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') { mobileOpen.value = false; return; }
  if (event.key !== 'Tab') return;
  const focusable = [...document.querySelectorAll<HTMLElement>('.mobile-drawer a, .mobile-drawer button, .mobile-drawer input')];
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
}
onBeforeUnmount(() => { if (mobileOpen.value) document.body.style.overflow = originalOverflow; });
const selectionCount = computed(() => store.dashboard?.snapshot?.selectionCount ?? store.candidates.length);

function metricTone(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return 'flat';
  return value > 0 ? 'positive' : 'negative';
}

const publicNav = [
  { label: '研究实验室', to: '/research' },
  { label: '研究候选', to: '/#daily-picks' },
  { label: '市场观察', to: '/#observation-pool' },
  { label: '验证闭环', to: '/#validation' },
  { label: '研究方法', to: '/#method' },
  { label: 'AI学院', to: '/#academy' }
];

const researchNav = [
  { label: '全市场资料', to: '/stock-research', icon: Search },
  { label: '市场总览', to: '/workspace', icon: LayoutDashboard },
  { label: '每日候选', to: '/workspace#daily-picks', icon: Sparkles, count: () => Math.min(store.candidates.length, 3) },
  { label: '最终研究池', to: '/workspace#observation-pool', icon: ListFilter, count: () => selectionCount.value },
  { label: '个股研究', to: '/analysis', icon: Microscope },
  { label: '模拟组合', to: '/paper', icon: BriefcaseBusiness }
];

const operationsNav = [
  { label: '研究实验室', to: '/research', icon: Microscope },
  { label: '验证闭环', to: '/workspace#validation-loop', icon: Workflow },
  { label: '研究报告', to: '/reports', icon: FileText },
  { label: '任务中心', to: '/tasks', icon: ClipboardCheck },
  { label: '收藏股票', to: '/favorites', icon: Heart }
];

const validation = computed(() => store.dashboard?.validation ?? null);
const passedGates = computed(() => Object.values(validation.value?.promotionGates ?? {}).filter(Boolean).length);
const totalGates = computed(() => Object.keys(validation.value?.promotionGates ?? {}).length);
const isWorkspaceShell = computed(() => Boolean(route.meta.auth && store.user));
const dataDate = computed(() => displayDate(store.dashboard?.snapshot?.quoteTradeDate));
const decisionAt = computed(() => displayDateTime(store.dashboard?.snapshot?.decisionAt));
const snapshotSyncedAt = computed(() => displayDateTime(store.dashboard?.snapshotSyncedAt));
const publicDataStatus = computed(() => publicResearchDataStatus(store.dashboard));
const routeTitle = computed(() => ({
  'stock-research': '全市场个股资料',
  research: '研究实验室',
  workspace: '市场总览',
  analysis: '个股研究',
  reports: '研究报告',
  tasks: '任务中心',
  favorites: '收藏股票',
  paper: '模拟组合',
  admin: '系统管理'
} as Record<string, string>)[String(route.name)] ?? '研究工作台');

async function logout() {
  shellMessage.value = '';
  try { await store.logout(); } catch (error) { shellMessage.value = userFacingError(error); }
  mobileOpen.value = false;
  await router.push('/');
}

async function refreshData() {
  await store.bootstrap();
}

async function submitSearch() {
  const query = normalizeCandidateSearch(searchQuery.value);
  if (!query) return;
  searchQuery.value = '';
  mobileOpen.value = false;
  if (/^\d{6}$/.test(query)) await router.push({ path: '/analysis', query: { code: query } });
  else await router.push({ path: store.user ? '/workspace' : '/', hash: '#observation-pool', query: { q: query } });
}

function publicNavActive(to: string): boolean {
  const [path, hash = ''] = to.split('#');
  if (route.path === '/' && !route.hash && to === '/#daily-picks') return true;
  return route.path === path && route.hash === (hash ? `#${hash}` : '');
}

function workspaceNavActive(to: string): boolean {
  const [path, hash = ''] = to.split('#');
  if (route.path !== path) return false;
  return hash ? route.hash === `#${hash}` : !route.hash;
}

</script>

<template>
  <a class="skip-link" href="#main-content">跳至主要内容</a>
  <div v-if="isWorkspaceShell" class="terminal-shell">
    <aside class="terminal-sidebar">
      <RouterLink to="/workspace" class="terminal-brand" aria-label="守一量化研究工作台首页">
        <span class="terminal-brand-mark">守一</span>
        <span><strong>量化研究平台</strong><small>SHOUYI QUANT RESEARCH</small></span>
      </RouterLink>

      <div class="terminal-nav-heading">研究工作区</div>
      <nav class="terminal-nav" aria-label="研究工作区导航">
        <RouterLink v-for="item in researchNav" :key="item.label" :to="item.to" :class="{ active: workspaceNavActive(item.to) }">
          <component :is="item.icon" :size="17" />
          <span>{{ item.label }}</span>
          <small v-if="item.count" class="terminal-nav-count">{{ item.count() }}</small>
        </RouterLink>
      </nav>

      <div class="terminal-nav-heading">验证与运营</div>
      <nav class="terminal-nav" aria-label="验证与运营导航">
        <RouterLink v-for="item in operationsNav" :key="item.label" :to="item.to" :class="{ active: workspaceNavActive(item.to) }">
          <component :is="item.icon" :size="17" /><span>{{ item.label }}</span>
        </RouterLink>
        <RouterLink v-if="store.isAdmin" to="/admin" :class="{ active: route.name === 'admin' }">
          <Settings :size="17" /><span>系统管理</span>
        </RouterLink>
      </nav>

      <div class="terminal-sidebar-status">
        <strong :class="{ offline: !store.dashboard?.worker.online }">{{ store.dashboard?.worker.online ? '分析节点在线' : '分析节点待机' }}</strong>
        <span>行情数据日 {{ dataDate }}</span>
        <span>选股决策 {{ decisionAt }}</span>
        <span>平台同步 {{ snapshotSyncedAt }}</span>
        <span>更新规则 {{ SNAPSHOT_PUBLISH_SCHEDULE_LABEL }}</span>
        <span>待处理 {{ store.dashboard?.worker.queuedJobs ?? 0 }} 个任务</span>
      </div>
    </aside>

    <div class="terminal-canvas">
      <header class="terminal-topbar">
        <button class="terminal-mobile-menu" type="button" title="打开导航" :aria-expanded="mobileOpen" aria-label="打开导航" @click="mobileOpen = true"><Menu :size="20" /></button>
        <div class="terminal-breadcrumb">研究首页 / <strong>{{ routeTitle }}</strong></div>
        <form class="terminal-search" role="search" @submit.prevent="submitSearch">
          <button class="search-submit" type="submit" title="搜索" aria-label="提交搜索"><Search :size="15" /></button>
          <input v-model="searchQuery" aria-label="搜索股票" placeholder="搜索股票代码、名称或行业" />
        </form>
        <button class="terminal-icon-button" type="button" title="刷新数据" aria-label="刷新数据" :disabled="store.loading" @click="refreshData"><RefreshCw :size="17" /></button>
        <RouterLink to="/workspace" class="terminal-user"><span>{{ store.user?.displayName.slice(0, 1) }}</span><strong>{{ store.user?.displayName }}</strong><ChevronDown :size="14" /></RouterLink>
        <button class="terminal-icon-button" type="button" title="退出登录" aria-label="退出登录" @click="logout"><LogOut :size="17" /></button>
      </header>

      <section class="terminal-ticker" aria-label="研究关键指标">
        <div><small>最终研究池</small><strong>{{ selectionCount }}</strong></div>
        <div><small>T+5 前向样本</small><strong>{{ validation?.sampleCount ?? '--' }}</strong></div>
        <div><small>独立信号日</small><strong>{{ validation?.signalDateCount ?? '--' }}</strong></div>
        <div><small>近 10 日等权净收益</small><strong :class="metricTone(validation?.recentMeanNetPct)">{{ displaySignedPct(validation?.recentMeanNetPct) }}</strong></div>
        <div><small>生产准入门</small><strong class="warning">{{ passedGates }} / {{ totalGates || '--' }}</strong></div>
      </section>

      <main id="main-content" class="terminal-main" tabindex="-1"><div class="page-container"><LoadState :loading="store.loading" :error="store.error || store.authError" @retry="store.bootstrap()" /></div><RouterView /></main>
    </div>

    <div v-if="mobileOpen" class="mobile-overlay" @click.self="mobileOpen = false" @keydown="drawerKeydown">
      <aside role="dialog" aria-modal="true" aria-label="网站导航" class="mobile-drawer terminal-mobile-drawer">
        <div class="drawer-head"><strong>研究工作台</strong><button class="icon-button" type="button" title="关闭导航" aria-label="关闭导航" @click="mobileOpen = false"><X :size="20" /></button></div>
        <form class="drawer-search field" role="search" @submit.prevent="submitSearch"><label for="drawer-query">查找研究候选</label><input id="drawer-query" v-model="searchQuery" type="search" placeholder="股票代码、名称或行业" /><button class="command-button" type="submit">搜索</button></form>
        <nav>
          <RouterLink v-for="item in researchNav" :key="item.label" :to="item.to" @click="mobileOpen = false"><component :is="item.icon" :size="18" />{{ item.label }}<ChevronRight :size="16" /></RouterLink>
          <div class="drawer-divider">验证与运营</div>
          <RouterLink v-for="item in operationsNav" :key="item.label" :to="item.to" @click="mobileOpen = false"><component :is="item.icon" :size="18" />{{ item.label }}<ChevronRight :size="16" /></RouterLink>
          <RouterLink v-if="store.isAdmin" to="/admin" @click="mobileOpen = false"><Settings :size="18" />系统管理<ChevronRight :size="16" /></RouterLink>
        </nav>
      </aside>
    </div>
  </div>

  <div v-else class="public-app-shell">
    <div class="public-marketbar">
      <div>
        <span class="public-market-status">{{ publicDataStatus }} · 行情数据日 {{ dataDate }}<span class="public-sync-time"> · 平台同步 {{ snapshotSyncedAt }}</span></span>
        <span class="public-market-metrics">最终研究池 <strong>{{ selectionCount }}</strong></span>
        <span class="public-market-metrics">T+5 样本 <strong>{{ validation?.sampleCount ?? '--' }}</strong></span>
        <span class="public-market-metrics">近 10 日净收益 <strong :class="metricTone(validation?.recentMeanNetPct)">{{ displaySignedPct(validation?.recentMeanNetPct) }}</strong></span>
        <span class="public-market-metrics">生产准入 <strong>{{ passedGates }} / {{ totalGates || '--' }}</strong></span>
      </div>
    </div>

    <header class="public-topbar">
      <div class="public-topbar-inner">
        <RouterLink to="/" class="public-brand" aria-label="守一量化研究平台首页">
          <span class="public-brand-mark">守一</span>
          <span><strong>量化研究平台</strong><small>SHOUYI QUANT RESEARCH</small></span>
        </RouterLink>
        <nav class="public-desktop-nav" aria-label="主导航">
          <RouterLink v-for="item in publicNav" :key="item.label" :to="item.to" :class="{ active: publicNavActive(item.to) }">{{ item.label }}</RouterLink>
        </nav>
        <form class="public-search" role="search" @submit.prevent="submitSearch"><button class="search-submit" type="submit" title="搜索" aria-label="提交搜索"><Search :size="15" /></button><input v-model="searchQuery" aria-label="搜索股票" placeholder="搜索股票代码、名称或行业" /></form>
        <div class="public-actions">
          <template v-if="store.user">
            <RouterLink to="/workspace" class="public-login"><CircleUserRound :size="16" />{{ store.user.displayName }}</RouterLink>
            <button class="public-icon-button" type="button" title="退出登录" aria-label="退出登录" @click="logout"><LogOut :size="17" /></button>
          </template>
          <template v-else>
            <RouterLink to="/login" class="public-login"><LogIn :size="15" />登录</RouterLink>
            <RouterLink :to="{ path: '/login', query: { mode: 'register' } }" class="public-register"><UserPlus :size="15" />注册账号</RouterLink>
          </template>
          <button class="public-icon-button public-mobile-menu" type="button" title="打开导航" :aria-expanded="mobileOpen" aria-label="打开导航" @click="mobileOpen = true"><Menu :size="20" /></button>
        </div>
      </div>
    </header>

    <div v-if="mobileOpen" class="mobile-overlay" @click.self="mobileOpen = false" @keydown="drawerKeydown">
      <aside role="dialog" aria-modal="true" aria-label="网站导航" class="mobile-drawer">
        <div class="drawer-head"><strong>导航</strong><button class="icon-button" type="button" title="关闭导航" aria-label="关闭导航" @click="mobileOpen = false"><X :size="20" /></button></div>
        <form class="drawer-search field" role="search" @submit.prevent="submitSearch"><label for="drawer-query">查找研究候选</label><input id="drawer-query" v-model="searchQuery" type="search" placeholder="股票代码、名称或行业" /><button class="command-button" type="submit">搜索</button></form>
        <nav>
          <RouterLink v-for="item in publicNav" :key="item.label" :to="item.to" @click="mobileOpen = false"><BookOpen :size="18" />{{ item.label }}<ChevronRight :size="16" /></RouterLink>
          <template v-if="store.user">
            <div class="drawer-divider">研究工作台</div>
            <RouterLink to="/workspace" @click="mobileOpen = false"><LayoutDashboard :size="18" />进入工作台<ChevronRight :size="16" /></RouterLink>
          </template>
        </nav>
      </aside>
    </div>

    <main id="main-content" class="public-route-main" tabindex="-1"><div class="page-container"><LoadState :error="store.authError || shellMessage" @retry="store.bootstrap()" /></div><RouterView /></main>
    <footer class="public-footer">
      <span><strong>守一量化研究平台</strong> · 仅供数据研究，不构成投资建议</span>
      <span class="public-footer-links"><RouterLink to="/terms">用户协议</RouterLink><RouterLink to="/privacy">隐私政策</RouterLink><RouterLink to="/risk-disclosure">风险揭示</RouterLink></span>
      <span>守一AI人工智能学院 · AI编程课 · 微信 <strong>YGR891004</strong></span>
    </footer>
  </div>
</template>

<style scoped>
.terminal-shell { min-height: 100vh; display: grid; grid-template-columns: 220px minmax(0, 1fr); background: #eef2f5; }
.terminal-sidebar { position: sticky; top: 0; z-index: 60; height: 100vh; display: flex; flex-direction: column; overflow-y: auto; color: #dbe6ed; background: #132638; border-right: 1px solid #223a4d; }
.terminal-brand { min-height: 66px; padding: 0 18px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #294153; }
.terminal-brand-mark { display: grid; place-items: center; flex: 0 0 34px; width: 34px; height: 34px; color: #fff; background: #0d7475; border: 1px solid #6dc2c2; border-radius: 6px; font-size: 13px; font-weight: 850; }
.terminal-brand strong, .terminal-brand small { display: block; }.terminal-brand strong { color: #fff; font-size: 14px; }.terminal-brand small { margin-top: 3px; color: #91a7b5; font-size: 12px; }
.terminal-nav-heading { padding: 18px 12px 7px; color: #758d9c; font-size: 12px; font-weight: 750; }
.terminal-nav { display: grid; gap: 3px; padding: 0 10px; }
.terminal-nav a { min-height: 40px; padding: 0 11px; display: flex; align-items: center; gap: 10px; color: #b8c7d1; border-radius: 5px; font-size: 12px; }
.terminal-nav a:hover, .terminal-nav a.active { color: #fff; background: #1f3d50; }.terminal-nav a.active { box-shadow: inset 3px 0 #45b7b7; }
.terminal-nav-count { margin-left: auto; min-width: 20px; height: 18px; padding: 0 5px; color: #0e555f; background: #d9edf0; border-radius: 9px; font-size: 12px; font-weight: 850; line-height: 18px; text-align: center; }
.terminal-sidebar-status { margin-top: auto; padding: 16px 18px 20px; display: grid; gap: 4px; color: #8fa4b1; border-top: 1px solid #294153; font-size: 12px; }
.terminal-sidebar-status strong { margin-bottom: 3px; color: #cfe5df; }.terminal-sidebar-status strong::before { content: ''; display: inline-block; width: 7px; height: 7px; margin-right: 7px; background: #45b987; border-radius: 50%; }.terminal-sidebar-status strong.offline { color: #e6c7a3; }.terminal-sidebar-status strong.offline::before { background: #c78a3d; }
.terminal-canvas { min-width: 0; }
.terminal-topbar { min-height: 66px; padding: 0 18px; display: flex; align-items: center; gap: 12px; background: #fff; border-bottom: 1px solid #dce3e8; }
.terminal-breadcrumb { min-width: 150px; color: #697785; font-size: 12px; }.terminal-breadcrumb strong { color: #182633; }
.terminal-search { width: min(430px, 45vw); height: 36px; margin-left: auto; padding: 0 11px; display: flex; align-items: center; gap: 8px; color: #80909c; background: #f3f6f8; border: 1px solid #dbe3e8; border-radius: 5px; }
.terminal-search input { min-width: 0; flex: 1; border: 0; outline: 0; color: #1c2b39; background: transparent; }
.search-submit { display: grid; place-items: center; flex: 0 0 22px; width: 22px; height: 22px; padding: 0; color: #748691; background: transparent; border: 0; border-radius: 3px; cursor: pointer; }.search-submit:hover { color: #0c6f72; background: #e5eeee; }
.terminal-icon-button, .public-icon-button { display: grid; place-items: center; flex: 0 0 36px; width: 36px; height: 36px; padding: 0; color: #425463; background: #fff; border: 1px solid #d7dfe5; border-radius: 5px; cursor: pointer; }
.terminal-icon-button:disabled { opacity: .55; cursor: wait; }
.terminal-user { display: flex; align-items: center; gap: 7px; color: #354653; white-space: nowrap; font-size: 12px; }.terminal-user > span { display: grid; place-items: center; width: 32px; height: 32px; color: #0b6669; background: #dcebee; border-radius: 50%; font-weight: 850; }
.terminal-mobile-menu { display: none; place-items: center; width: 36px; height: 36px; padding: 0; color: #425463; background: #fff; border: 1px solid #d7dfe5; border-radius: 5px; }
.terminal-ticker { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); min-height: 58px; background: #fff; border-bottom: 1px solid #dce3e8; }
.terminal-ticker > div { padding: 8px 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; border-right: 1px solid #e5eaee; }.terminal-ticker > div:last-child { border-right: 0; }
.terminal-ticker small { color: #74818d; font-size: 12px; }.terminal-ticker strong { margin-top: 3px; color: #1c2b37; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 15px; }.terminal-ticker .positive, .public-marketbar .positive { color: #16805a; }.terminal-ticker .negative, .public-marketbar .negative { color: #d16c69; }.terminal-ticker .flat, .public-marketbar .flat { color: #b7c4cb; }.terminal-ticker .warning { color: #a76408; }
.terminal-main { min-width: 0; padding: 18px 0 34px; }

.public-app-shell { min-height: 100vh; display: flex; flex-direction: column; background: #f3f6f8; }
.public-marketbar { min-height: 34px; padding: 5px 16px; display: flex; align-items: center; color: #b9c9d2; background: #102838; font-size: 12px; }
.public-marketbar > div { width: min(1360px, 100%); margin: 0 auto; display: flex; align-items: center; justify-content: flex-end; gap: 22px; }
.public-market-status { margin-right: auto; color: #d6e5e9; }.public-market-status::before { content: ''; display: inline-block; width: 7px; height: 7px; margin-right: 7px; background: #50bd8e; border-radius: 50%; box-shadow: 0 0 0 3px rgba(80,189,142,.14); }
.public-market-metrics strong { margin-left: 4px; color: #fff; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.public-topbar { position: sticky; top: 0; z-index: 50; background: #fff; border-bottom: 1px solid #d9e1e6; }
.public-topbar-inner { width: min(1360px, calc(100% - 32px)); min-height: 68px; margin: 0 auto; display: flex; align-items: center; gap: 18px; }
.public-brand { min-width: 210px; display: flex; align-items: center; gap: 10px; }.public-brand-mark { display: grid; place-items: center; width: 36px; height: 36px; color: #fff; background: #0a6d71; border-radius: 6px; font-size: 13px; font-weight: 850; }.public-brand strong, .public-brand small { display: block; }.public-brand strong { color: #112836; font-size: 15px; }.public-brand small { margin-top: 2px; color: #788994; font-size: 12px; }
.public-desktop-nav { align-self: stretch; display: flex; align-items: stretch; }.public-desktop-nav a { position: relative; padding: 0 11px; display: flex; align-items: center; color: #52636e; font-size: 12px; font-weight: 650; white-space: nowrap; }.public-desktop-nav a.active { color: #0e6569; }.public-desktop-nav a.active::after { content: ''; position: absolute; right: 11px; bottom: 0; left: 11px; height: 3px; background: #0e7779; }
.public-search { width: min(285px, 25vw); height: 35px; margin-left: auto; padding: 0 10px; display: flex; align-items: center; gap: 8px; color: #87949e; background: #f6f8fa; border: 1px solid #d4dde3; border-radius: 4px; }.public-search input { min-width: 0; flex: 1; border: 0; outline: 0; color: #20313d; background: transparent; }
.public-actions { display: flex; align-items: center; gap: 7px; }.public-login, .public-register { min-height: 35px; padding: 0 11px; display: inline-flex; align-items: center; gap: 6px; border: 1px solid #b9c8d0; border-radius: 4px; color: #294958; background: #fff; font-size: 12px; font-weight: 750; white-space: nowrap; }.public-register { color: #fff; background: #0b7073; border-color: #0b7073; }
.public-mobile-menu { display: none; }
.public-route-main { flex: 1; min-width: 0; }
.public-footer { padding: 18px max(16px, calc((100% - 1360px) / 2)); display: flex; justify-content: space-between; gap: 20px; color: #90a1ac; background: #122735; font-size: 12px; }.public-footer strong { color: #dce8ec; }
.public-footer-links { display: flex; gap: 14px; }.public-footer-links a { color: #c7d6dc; }.public-footer-links a:hover { color: #fff; }

@media (max-width: 1160px) {
  .public-desktop-nav { display: none; }.public-mobile-menu { display: grid; }.public-brand { min-width: auto; }
}
@media (max-width: 900px) {
  .terminal-shell { display: block; }.terminal-sidebar { display: none; }.terminal-mobile-menu { display: grid; }.terminal-breadcrumb { min-width: auto; }.terminal-search { flex: 1; width: auto; }.terminal-user strong, .terminal-user > svg { display: none; }
}
@media (max-width: 760px) {
  .terminal-icon-button, .public-icon-button, .terminal-mobile-menu { flex: 0 0 44px; width: 44px; height: 44px; }
  .terminal-search, .public-login, .public-register { min-height: 44px; }
  .terminal-search { min-width: 0; }
  .public-marketbar > div { justify-content: center; }.public-market-metrics, .public-sync-time { display: none; }.public-market-status { margin-right: 0; }
  .public-topbar-inner { width: calc(100% - 20px); min-height: 58px; gap: 8px; }.public-brand strong { font-size: 13px; }.public-brand small { display: none; }.public-brand-mark { width: 32px; height: 32px; }.public-search { display: none; }.public-actions { margin-left: auto; }.public-login, .public-register { padding: 0 9px; }
  .terminal-topbar { min-height: 58px; padding: 0 10px; gap: 7px; }.terminal-breadcrumb { display: none; }.terminal-user { display: none; }.terminal-ticker { grid-template-columns: repeat(3, 1fr); }.terminal-ticker > div:nth-child(n+4) { display: none; }.terminal-main { padding-top: 12px; }
  .public-footer { flex-direction: column; gap: 6px; }
}
@media (max-width: 420px) {
  .public-login { display: none; }.terminal-search input::placeholder { color: transparent; }
}
</style>
